import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import {
  Search,
  DeleteOutline,
  ExpandMore,
  ChevronRight,
  FilterList,
  Check,
} from "@mui/icons-material";
import { designColors as colors } from "services/constants/designTokens";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";

export const employeeKey = (user) => String(user.user_id ?? user.id);
export const employeeDepartment = (user) =>
  user.department_name ||
  user.department?.department_name ||
  (typeof user.department === "string" ? user.department : "") ||
  "Unassigned";
// A person can belong to several departments (Add Users in User Group
// passes them as department_names). They are then listed under each one
// instead of under a single made-up "A, B, C" department.
export const employeeDepartments = (user) =>
  Array.isArray(user.department_names) && user.department_names.length
    ? user.department_names
    : [employeeDepartment(user)];
// Short text for a cell: the first two departments, then "+N".
export const employeeDepartmentLabel = (user) => {
  const names = employeeDepartments(user);
  return names.length <= 2
    ? names.join(", ")
    : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
};
export const employeeName = (user) =>
  user.user_name || user.employee_name || user.name || "Employee";
// The employee's own company. Only set by screens that list employees of
// several companies together (Add Users in User Group).
export const employeeCompany = (user) => user.home_company || "";
// Advanced Filter fields next to Department. Screens can pass their own
// (filterFields prop, e.g. Salary Revision's Payroll Group / Template /
// Salary Type); these are the Bulk Reprocess ones.
const defaultFilterFields = [
  { key: "grade", label: "Shift Grade", allLabel: "All grades", field: "shift_grade_name" },
  { key: "shift", label: "Shift Group", allLabel: "All shift groups", field: "shift_group_name" },
  { key: "designation", label: "Designation", allLabel: "All designations", field: "designation_name" },
];
const blankFilters = (fields) => ({
  department: [],
  ...Object.fromEntries(fields.map((item) => [item.key, []])),
});
const emptyFilters = blankFilters(defaultFilterFields);
const allDepartmentsOption = { label: "All departments" };
const matchesFilter = (selection, value) =>
  Array.isArray(selection)
    ? !selection.length || selection.includes(value)
    : !selection || selection === value;

export const filterEmployees = (users, search, filters = emptyFilters, fields = defaultFilterFields) => {
  const query = search.trim().toLowerCase();
  return users.filter(
    (user) =>
      `${employeeName(user)} ${user.employee_id || ""} ${employeeDepartments(user).join(" ")} ${employeeCompany(user)}`
        .toLowerCase()
        .includes(query) &&
      (!filters.department.length ||
        employeeDepartments(user).some((name) =>
          filters.department.includes(name))) &&
      fields.every((item) => matchesFilter(filters[item.key] ?? [], user[item.field])),
  );
};

export const toggleEmployees = (selected, users, checked) => {
  const ids = new Set(users.map(employeeKey));
  return checked
    ? [
        ...selected,
        ...users.filter(
          (user) =>
            !selected.some((item) => employeeKey(item) === employeeKey(user)),
        ),
      ]
    : selected.filter((user) => !ids.has(employeeKey(user)));
};

// By Department: the Select All, department and employee checkboxes share
// one left edge -- the department row's 4px padding plus its expand button.
const departmentToggleSize = 34;
const departmentCheckboxInset = `${4 + departmentToggleSize}px`;
const frame = {
  border: `1px solid ${colors.slate200}`,
  borderRadius: "8px",
  overflow: "hidden",
};
const tableSx = {
  "& th": {
    bgcolor: colors.slateBg,
    color: colors.slate900,
    fontWeight: 600,
    whiteSpace: "nowrap",
    fontSize: 12,
  },
  "& td": { fontSize: 12, color: colors.slate700 },
  "& td, & th": { borderColor: colors.slate100, py: 1, px: 1 },
};

const avatarPalette = ["#EDE9FE", "#DBEAFE", "#DCFCE7", "#FEF3C7", "#FFE4E6"];
const employeeAvatarColor = (user) =>
  avatarPalette[
    [...employeeKey(user)].reduce(
      (sum, character) => sum + character.charCodeAt(0),
      0,
    ) % avatarPalette.length
  ];
const employeeInitials = (name) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

// showId: puts the employee ID on a second line under the name (used when
// the table has no Employee ID column of its own).
function EmployeeIdentity({ user, showId = false }) {
  const name = employeeName(user);
  return (
    <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Mui.Avatar
        sx={{
          width: 30,
          height: 30,
          fontSize: 11,
          fontWeight: 600,
          bgcolor: employeeAvatarColor(user),
          color: colors.slate700,
          flexShrink: 0,
        }}
      >
        {employeeInitials(name)}
      </Mui.Avatar>
      {showId ? (
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.slate900 }}>
            {name}
          </Mui.Typography>
          <Mui.Typography sx={{ fontSize: 11, color: colors.slate600 }}>
            {user.employee_id || "—"}
          </Mui.Typography>
        </Mui.Box>
      ) : (
        <Mui.Typography sx={{ fontSize: 12 }}>{name}</Mui.Typography>
      )}
    </Mui.Stack>
  );
}

// Selected-employee "box": avatar + name/ID/department + a remove action,
// as one compact card. Exported (and kept free of any Bulk-Reprocess-only
// assumptions) so the other bulk-selection screens can import this same
// component once they're updated, instead of each hand-rolling its own
// selected-employee list.
export function SelectedEmployeeBox({ user, onRemove }) {
  const name = employeeName(user);
  return (
    <Mui.Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        pr: 0.5,
        border: `1px solid ${colors.slate200}`,
        borderRadius: "10px",
        bgcolor: colors.white,
        minWidth: 0,
      }}
    >
      <Mui.Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 11,
          fontWeight: 600,
          bgcolor: employeeAvatarColor(user),
          color: colors.slate700,
          flexShrink: 0,
        }}
      >
        {employeeInitials(name)}
      </Mui.Avatar>
      <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
        {/* Styled tooltip (only when the name is cut off) instead of the
            browser's native title tooltip. */}
        <Mui.Typography
          component="div"
          sx={{ fontSize: 12.5, fontWeight: 600, color: colors.slate900 }}
        >
          <TruncatedTableValue value={name} />
        </Mui.Typography>
        <Mui.Typography
          sx={{
            fontSize: 11,
            color: colors.slate600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.employee_id || "—"} · {employeeDepartmentLabel(user)}
        </Mui.Typography>
      </Mui.Box>
      {onRemove && (
        <Mui.IconButton
          size="small"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
          sx={{ flexShrink: 0, p: 0.5 }}
        >
          <DeleteOutline sx={{ fontSize: 16 }} />
        </Mui.IconButton>
      )}
    </Mui.Box>
  );
}

export function BulkReprocessEmployeePicker({
  users,
  selected,
  onChange,
  loading,
  error,
  companySelected,
  expanded: panelExpanded = false,
  // Wording overrides so other screens (e.g. Add Users in User Group) can
  // reuse this picker; the defaults are the Bulk Reprocess copy.
  noCompanyMessage = "No company selected. Choose a company from the top bar to load employees.",
  emptySelectionText = "Select employees to include in reprocessing.",
  // showCompany: for lists that mix employees of several companies (Add
  // Users in User Group). Adds a Company column in place of the Employee ID
  // column and moves the employee ID under the name.
  showCompany = false,
  // departmentGroups + onLoadDepartment: opt-in lazy "By Department" tab
  // (Add Users in User Group). The rows come from the caller's department
  // list ({ key, name, members, loading, error }) instead of being worked
  // out from `users`, and a department's employees are only fetched --
  // onLoadDepartment(key) resolves to them -- when it is expanded or ticked.
  // `members` stays undefined until that department has been loaded.
  departmentGroups,
  onLoadDepartment,
  departmentsLoading = false,
  departmentsError = "",
  // filterFields: Advanced Filter fields beside Department
  // ([{ key, label, allLabel, field, options? }]; field is the user
  // property, options a fixed list instead of the users' own values).
  // Keep the array stable (a module constant or useMemo).
  filterFields = defaultFilterFields,
  // onFilterChange(filters): the Advanced Filter form as it is edited, for
  // screens whose filter options depend on each other (Salary Revision).
  onFilterChange,
}) {
  const noFilters = useMemo(() => blankFilters(filterFields), [filterFields]);
  const allFilterOptions = useMemo(
    () => Object.fromEntries(filterFields.map((item) => [item.key, { label: item.allLabel }])),
    [filterFields],
  );
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState(noFilters);
  const [appliedFilters, setAppliedFilters] = useState(noFilters);
  const selectedIds = new Set(selected.map(employeeKey));
  const options = (field) =>
    [
      ...new Set(
        users
          .map((user) => user[field])
          .filter((value) => typeof value === "string" && value),
      ),
    ].sort();
  const departments = useMemo(
    () => [...new Set(users.flatMap(employeeDepartments))].sort(),
    [users],
  );
  const lazyDepartments = Array.isArray(departmentGroups);
  const departmentRows = (
    lazyDepartments
      ? departmentGroups
      : departments.map((name) => ({
          key: name,
          name,
          members: users.filter((user) =>
            employeeDepartments(user).includes(name),
          ),
        }))
  ).filter((row) =>
    String(row.name ?? "")
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const uniqueEmployees = (list) =>
    list.filter(
      (user, index) =>
        list.findIndex((item) => employeeKey(item) === employeeKey(user)) ===
        index,
    );
  // Employees of the matching departments that are loaded so far.
  const departmentMembers = uniqueEmployees(
    departmentRows.flatMap((row) => row.members || []),
  );
  const allDepartmentsLoaded = departmentRows.every((row) => row.members);
  // A load can finish after other ticks were made, so read the latest picks.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const loadDepartment = (row) =>
    row.members
      ? Promise.resolve(row.members)
      : Promise.resolve(onLoadDepartment?.(row.key)).then((list) => list || []);
  // Ticking a department that is not loaded yet loads it first.
  const toggleDepartments = (rows, checked) =>
    Promise.all(rows.map(loadDepartment))
      .then((lists) =>
        onChange(
          toggleEmployees(
            selectedRef.current,
            uniqueEmployees(lists.flat()),
            checked,
          ),
        ),
      )
      // The failed row shows its own error.
      .catch(() => {});
  const matching = filterEmployees(
    users,
    tab === 1 ? "" : search,
    tab === 2 ? appliedFilters : noFilters,
    filterFields,
  );
  // Expanded gives the employee table enough room to just show every
  // matching employee at once — paging only earns its keep in the
  // narrower, shorter side-panel drawer.
  const pageCount = panelExpanded
    ? 1
    : Math.max(1, Math.ceil(matching.length / 10));
  const currentPage = panelExpanded ? 1 : Math.min(page, pageCount);
  const visible = panelExpanded
    ? matching
    : matching.slice((currentPage - 1) * 10, currentPage * 10);
  const toggle = (list, checked) =>
    onChange(toggleEmployees(selected, list, checked));
  const pendingSelectBox = (rows, label, partlySelected = false) => (
    <Mui.Checkbox
      size="small"
      inputProps={{ "aria-label": label }}
      checked={false}
      indeterminate={partlySelected}
      disabled={!rows.length || loading || rows.some((row) => row.loading)}
      onChange={() => toggleDepartments(rows, !partlySelected)}
      sx={{
        p: 0.5,
        "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: colors.teal },
      }}
    />
  );
  const selectBox = (list, label) => {
    const count = list.filter((user) =>
      selectedIds.has(employeeKey(user)),
    ).length;
    return (
      <Mui.Checkbox
        size="small"
        inputProps={{ "aria-label": label }}
        checked={list.length > 0 && count === list.length}
        indeterminate={count > 0 && count < list.length}
        disabled={!list.length || loading}
        onChange={(event) => toggle(list, event.target.checked)}
        sx={{
          p: 0.5,
          "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: colors.teal },
        }}
      />
    );
  };
  const searchInput = (
    <Mui.TextField
      size="small"
      fullWidth
      value={search}
      placeholder={
        tab === 1
          ? "Search departments..."
          : tab === 2
            ? "Search in results..."
            : showCompany
              ? "Search employees by name, ID, company or department..."
              : "Search employees by name, ID or department..."
      }
      inputProps={{
        "aria-label": tab === 1 ? "Search departments" : "Search employees",
      }}
      onChange={(event) => {
        setSearch(event.target.value);
        setPage(1);
      }}
      InputProps={{
        startAdornment: (
          <Mui.InputAdornment position="start">
            <Search sx={{ fontSize: 20, color: colors.slate400 }} />
          </Mui.InputAdornment>
        ),
      }}
    />
  );
  useEffect(() => {
    onFilterChange?.(filters);
    // onFilterChange is a plain callback from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const updateFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const filterFieldSx = {
    minWidth: 0,
    "& .MuiAutocomplete-inputRoot": { height: 40, flexWrap: "nowrap" },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      minWidth: "24px",
      width: 0,
      flex: "1 1 24px",
    },
  };
  const renderFilterTags = (values, getTagProps) => {
    const option = values[0];
    if (!option) return null;
    const label = typeof option === "string" ? option : option.label;
    const { key, ...tagProps } = getTagProps({ index: 0 });
    return (
      <Mui.Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          maxWidth: "calc(100% - 32px)",
          flexShrink: 1,
        }}
      >
        <Mui.Chip
          key={key}
          {...tagProps}
          label={label}
          title={label}
          size="small"
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            flexShrink: 1,
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
        {values.length > 1 && (
          <Mui.Box
            component="span"
            title={values
              .slice(1)
              .map((value) => (typeof value === "string" ? value : value.label))
              .join(", ")}
            sx={{ flexShrink: 0, fontSize: 12, ml: 0.5, whiteSpace: "nowrap" }}
          >
            +{values.length - 1}
          </Mui.Box>
        )}
      </Mui.Box>
    );
  };
  const renderFilterOption = (
    { key, ...optionProps },
    option,
    { selected },
  ) => (
    <Mui.Box
      component="li"
      key={key}
      {...optionProps}
      sx={{
        gap: 1,
        fontSize: 13,
        '&&[aria-selected="true"], &&[aria-selected="true"].Mui-focused, &&[aria-selected="true"]:hover':
          {
            bgcolor: colors.tealTint,
            color: colors.teal,
            fontWeight: 600,
          },
      }}
    >
      <Mui.Box component="span" sx={{ width: 20, height: 20, flexShrink: 0 }}>
        {selected && <Check aria-hidden="true" sx={{ fontSize: 20 }} />}
      </Mui.Box>
      <Mui.Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {typeof option === "string" ? option : option.label}
      </Mui.Box>
    </Mui.Box>
  );
  const filterSelect = (key, label, values) => (
    <Mui.Autocomplete
      multiple
      disableCloseOnSelect
      disablePortal
      sx={filterFieldSx}
      renderTags={renderFilterTags}
      renderOption={renderFilterOption}
      size="small"
      fullWidth
      options={[allFilterOptions[key], ...values]}
      value={filters[key].length ? filters[key] : [allFilterOptions[key]]}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      onChange={(_, value, reason, details) =>
        updateFilter(
          key,
          reason === "selectOption" && details?.option === allFilterOptions[key]
            ? []
            : value.filter((option) => option !== allFilterOptions[key]),
        )
      }
      renderInput={(params) => <Mui.TextField {...params} label={label} />}
    />
  );

  return (
    <Mui.Box
      sx={{
        "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13 },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.borderInput,
        },
      }}
    >
      <Mui.Tabs
        value={tab}
        variant="fullWidth"
        onChange={(_, value) => {
          // Every per-tab value resets on switch — search text, pagination, the
          // Advanced Filter form and its applied criteria, which department rows
          // are expanded, and (per explicit product decision) the selected
          // employees themselves — so each tab always starts completely clean
          // instead of carrying over a previous tab's picks.
          setTab(value);
          setSearch("");
          setPage(1);
          setFilters(noFilters);
          setAppliedFilters(noFilters);
          setExpanded({});
          onChange([]);
        }}
        sx={{
          mb: 2,
          borderBottom: `1px solid ${colors.slate200}`,
          "& .MuiTabs-indicator": { bgcolor: colors.teal },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
          },
          "& .Mui-selected": { color: `${colors.teal} !important` },
        }}
      >
        {["By Employee", "By Department", "Advanced Filter"].map(
          (label, index) => (
            <Mui.Tab
              key={label}
              label={label}
              id={`reprocess-tab-${index}`}
              aria-controls="reprocess-employee-options"
            />
          ),
        )}
      </Mui.Tabs>
      <Mui.Box
        role="tabpanel"
        id="reprocess-employee-options"
        aria-labelledby={`reprocess-tab-${tab}`}
      >
        {tab === 2 && (
          <Mui.Box sx={{ ...frame, p: 2, mb: 2 }}>
            <Mui.Typography sx={{ fontWeight: 600, fontSize: 13, mb: 2 }}>
              Filter Criteria
            </Mui.Typography>
            <Mui.Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <Mui.Autocomplete
                multiple
                disableCloseOnSelect
                disablePortal
                sx={filterFieldSx}
                renderTags={renderFilterTags}
                renderOption={renderFilterOption}
                size="small"
                options={[allDepartmentsOption, ...departments]}
                value={
                  filters.department.length
                    ? filters.department
                    : [allDepartmentsOption]
                }
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.label
                }
                onChange={(_, value, reason, details) =>
                  updateFilter(
                    "department",
                    reason === "selectOption" &&
                      details?.option === allDepartmentsOption
                      ? []
                      : value.filter(
                          (option) => option !== allDepartmentsOption,
                        ),
                  )
                }
                renderInput={(params) => (
                  <Mui.TextField {...params} label="Department" />
                )}
              />
              {filterFields.map((item) => (
                <React.Fragment key={item.key}>
                  {filterSelect(item.key, item.label, item.options ?? options(item.field))}
                </React.Fragment>
              ))}
            </Mui.Box>
            <Mui.Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
              sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
            >
              <SecondaryButton
                sx={{ minWidth: 90, minHeight: 38 }}
                onClick={() => {
                  setFilters(noFilters);
                  setAppliedFilters(noFilters);
                  setSearch("");
                  setPage(1);
                }}
              >
                Reset
              </SecondaryButton>
              <PrimaryButton
                sx={{ minWidth: 150, minHeight: 38 }}
                onClick={() => {
                  setAppliedFilters(filters);
                  setPage(1);
                }}
              >
                Search
              </PrimaryButton>
            </Mui.Stack>
          </Mui.Box>
        )}
        {/* alignItems: stretch (the grid default) already makes both columns
          match the row's height — the left column just had no border to
          make that visible, so it looked shorter than the bordered
          "Selected Employees" card next to it even though it wasn't. */}
        <Mui.Box
          sx={{
            display: "grid",
            alignItems: "stretch",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: panelExpanded
                ? "minmax(0, 1.2fr) minmax(0, 1fr)"
                : "minmax(0, 1fr)",
            },
            gap: 2,
          }}
        >
          <Mui.Box
            sx={{
              ...frame,
              minWidth: 0,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {tab === 2 && (
              <Mui.Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Mui.Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  Matching Employees ({matching.length})
                </Mui.Typography>
                <Mui.FormControlLabel
                  sx={{
                    mr: 0,
                    "& .MuiFormControlLabel-label": { fontSize: 12 },
                  }}
                  control={selectBox(matching, "Select all matching employees")}
                  label={`Select All (${matching.length})`}
                />
              </Mui.Stack>
            )}
            <Mui.Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              {searchInput}
              {tab === 0 && (
                <Mui.Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => {
                    setTab(2);
                    setSearch("");
                    setPage(1);
                  }}
                  sx={{
                    color: colors.slate700,
                    borderColor: colors.borderInput,
                    textTransform: "none",
                  }}
                >
                  Filter
                </Mui.Button>
              )}
            </Mui.Stack>
            {loading ? (
              <Mui.Box role="status" sx={{ p: 4, textAlign: "center" }}>
                <Mui.CircularProgress size={24} />
                <Mui.Typography sx={{ mt: 1, fontSize: 13 }}>
                  Loading employees...
                </Mui.Typography>
              </Mui.Box>
            ) : error ? (
              <Mui.Alert severity="error">{error}</Mui.Alert>
            ) : !companySelected ? (
              <Mui.Alert severity="info">{noCompanyMessage}</Mui.Alert>
            ) : tab === 1 && departmentsLoading ? (
              <Mui.Typography sx={{ p: 3, fontSize: 13 }}>
                Loading departments...
              </Mui.Typography>
            ) : tab === 1 && departmentsError ? (
              <Mui.Alert severity="error">{departmentsError}</Mui.Alert>
            ) : tab === 1 ? (
              <Mui.Box sx={frame}>
                <Mui.Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    pr: 1.5,
                    pl: departmentCheckboxInset,
                    bgcolor: colors.slateBg,
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  <Mui.FormControlLabel
                    control={
                      allDepartmentsLoaded
                        ? selectBox(
                            departmentMembers,
                            "Select all employees in matching departments",
                          )
                        : pendingSelectBox(
                            departmentRows,
                            "Select all employees in matching departments",
                            departmentMembers.some((user) =>
                              selectedIds.has(employeeKey(user)),
                            ),
                          )
                    }
                    label={
                      allDepartmentsLoaded
                        ? `Select All (${departmentMembers.length})`
                        : "Select All"
                    }
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: 12,
                        fontWeight: 600,
                      },
                    }}
                  />
                  <span>Total / Selected</span>
                </Mui.Box>
                {departmentRows.map((row) => {
                  const { key: rowKey, name } = row;
                  const members = row.members || [];
                  const count = members.filter((user) =>
                    selectedIds.has(employeeKey(user)),
                  ).length;
                  return (
                    <Mui.Box
                      key={rowKey}
                      sx={{ borderTop: `1px solid ${colors.slate200}` }}
                    >
                      <Mui.Stack
                        direction="row"
                        alignItems="center"
                        sx={{ px: 0.5, py: 0.75 }}
                      >
                        <Mui.IconButton
                          size="small"
                          aria-label={`Expand ${name}`}
                          aria-expanded={Boolean(expanded[rowKey])}
                          sx={{
                            width: departmentToggleSize,
                            height: departmentToggleSize,
                            flexShrink: 0,
                          }}
                          onClick={() => {
                            // Lazy rows fetch their employees on first expand
                            // (and again after a failed load).
                            if (!expanded[rowKey] && !row.members && !row.loading)
                              loadDepartment(row).catch(() => {});
                            setExpanded((current) => ({
                              ...current,
                              [rowKey]: !current[rowKey],
                            }));
                          }}
                        >
                          {expanded[rowKey] ? <ExpandMore /> : <ChevronRight />}
                        </Mui.IconButton>
                        {row.members
                          ? selectBox(members, `Select all in ${name}`)
                          : pendingSelectBox([row], `Select all in ${name}`)}
                        <Mui.Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            flex: 1,
                            ml: 0.5,
                          }}
                        >
                          {name}
                        </Mui.Typography>
                        <Mui.Typography sx={{ fontSize: 12, mr: 1 }}>
                          {row.members
                            ? `${members.length} / ${count} selected`
                            : row.loading
                              ? "Loading..."
                              : ""}
                        </Mui.Typography>
                      </Mui.Stack>
                      <Mui.Collapse in={Boolean(expanded[rowKey])} unmountOnExit>
                        <Mui.Box
                          sx={{
                            pl: departmentCheckboxInset,
                            // Same right edge as the header and the counts.
                            pr: 1.5,
                            pb: 1,
                            // Add Users in User Group shows every employee
                            // of an expanded department (user request
                            // 2026-10-08): no inner scrollbar, the panel
                            // itself scrolls. Bulk Reprocess keeps its cap.
                            ...(lazyDepartments
                              ? {}
                              : { maxHeight: 280, overflow: "auto" }),
                          }}
                        >
                          {row.error ? (
                            <Mui.Alert severity="error" sx={{ my: 0.5 }}>
                              {row.error}
                            </Mui.Alert>
                          ) : !row.members ? (
                            <Mui.Typography sx={{ py: 1, fontSize: 12 }}>
                              Loading employees...
                            </Mui.Typography>
                          ) : !members.length ? (
                            <Mui.Typography sx={{ py: 1, fontSize: 12 }}>
                              No employees to select in this department.
                            </Mui.Typography>
                          ) : null}
                          {members.map((user) => (
                            <Mui.Stack
                              key={employeeKey(user)}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ py: 0.5 }}
                            >
                              {selectBox(
                                [user],
                                `Select ${employeeName(user)}`,
                              )}
                              <EmployeeIdentity user={user} showId={showCompany} />
                              <Mui.Typography
                                sx={{ ml: "auto !important", pl: 1, fontSize: 12 }}
                              >
                                {showCompany ? employeeCompany(user) : user.employee_id}
                              </Mui.Typography>
                            </Mui.Stack>
                          ))}
                        </Mui.Box>
                      </Mui.Collapse>
                    </Mui.Box>
                  );
                })}
                {!departmentRows.length && (
                  <Mui.Typography sx={{ p: 3, fontSize: 13 }}>
                    No departments found.
                  </Mui.Typography>
                )}
              </Mui.Box>
            ) : (
              <>
                <Mui.TableContainer sx={frame}>
                  <Mui.Table
                    size="small"
                    sx={tableSx}
                    aria-label="Available employees"
                  >
                    <Mui.TableHead>
                      <Mui.TableRow>
                        <Mui.TableCell padding="checkbox">
                          {selectBox(matching, "Select all matching employees")}
                        </Mui.TableCell>
                        <Mui.TableCell>#</Mui.TableCell>
                        <Mui.TableCell>Employee Name</Mui.TableCell>
                        <Mui.TableCell>{showCompany ? "Company" : "Employee ID"}</Mui.TableCell>
                        <Mui.TableCell>Department</Mui.TableCell>
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {visible.map((user, index) => (
                        <Mui.TableRow
                          key={employeeKey(user)}
                          sx={{
                            bgcolor: selectedIds.has(employeeKey(user))
                              ? colors.tealTint
                              : "transparent",
                          }}
                        >
                          <Mui.TableCell padding="checkbox">
                            {selectBox([user], `Select ${employeeName(user)}`)}
                          </Mui.TableCell>
                          <Mui.TableCell>
                            {(currentPage - 1) * 10 + index + 1}
                          </Mui.TableCell>
                          <Mui.TableCell>
                            <EmployeeIdentity user={user} showId={showCompany} />
                          </Mui.TableCell>
                          <Mui.TableCell>
                            {showCompany
                              ? employeeCompany(user) || "—"
                              : user.employee_id || "—"}
                          </Mui.TableCell>
                          <Mui.TableCell>
                            {employeeDepartmentLabel(user)}
                          </Mui.TableCell>
                        </Mui.TableRow>
                      ))}
                      {!visible.length && (
                        <Mui.TableRow>
                          <Mui.TableCell
                            colSpan={5}
                            align="center"
                            sx={{ py: "32px !important" }}
                          >
                            No employees found.
                          </Mui.TableCell>
                        </Mui.TableRow>
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                </Mui.TableContainer>
                {panelExpanded ? (
                  <Mui.Typography
                    sx={{ mt: 1.5, fontSize: 12, color: colors.slate600 }}
                  >
                    Showing all {matching.length} employees
                  </Mui.Typography>
                ) : (
                  <>
                    <Mui.Typography
                      sx={{ mt: 1.5, fontSize: 12, color: colors.slate600 }}
                    >
                      Showing {matching.length ? (currentPage - 1) * 10 + 1 : 0}{" "}
                      to {Math.min(currentPage * 10, matching.length)} of{" "}
                      {matching.length} employees
                    </Mui.Typography>
                    <Mui.Pagination
                      size="small"
                      shape="rounded"
                      count={pageCount}
                      page={currentPage}
                      onChange={(_, value) => setPage(value)}
                      sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                        "& .Mui-selected": {
                          bgcolor: `${colors.teal} !important`,
                          color: "white",
                        },
                      }}
                    />
                  </>
                )}
              </>
            )}
          </Mui.Box>
          <Mui.Box
            sx={{
              ...frame,
              minWidth: 0,
              minHeight: 300,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Mui.Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: colors.slateBg,
                borderBottom: `1px solid ${colors.slate200}`,
                flexShrink: 0,
              }}
            >
              <Mui.Typography sx={{ flex: 1, fontWeight: 600, fontSize: 14 }}>
                Selected Employees ({selected.length})
              </Mui.Typography>
              <Mui.IconButton
                size="small"
                color="error"
                aria-label="Clear all selected employees"
                disabled={!selected.length}
                onClick={() => onChange([])}
              >
                <DeleteOutline fontSize="small" />
              </Mui.IconButton>
            </Mui.Stack>
            {/* No maxHeight/overflowY here — this used to scroll independently
              inside the panel's own scrollable body, which showed two
              vertical scrollbars stacked on each other. It now grows with
              the rest of the section and scrolls with the single outer
              panel scroll instead. */}
            <Mui.Box sx={{ p: 1.5 }}>
              {selected.length ? (
                <Mui.Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {selected.map((user) => (
                    <SelectedEmployeeBox
                      key={employeeKey(user)}
                      user={user}
                      onRemove={() => toggle([user], false)}
                    />
                  ))}
                </Mui.Box>
              ) : (
                <Mui.Typography
                  sx={{
                    py: 5,
                    textAlign: "center",
                    fontSize: 12,
                    color: colors.slate600,
                  }}
                >
                  {emptySelectionText}
                </Mui.Typography>
              )}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>
    </Mui.Box>
  );
}
